import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.55;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  /**
   * Handles cart add events to auto-open drawer if configured
   */
  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

// =========================================================================
// START – Dynamic Cart Recommendations Loading
// Feature: Dynamic Product Recommendations Merging
// Description: Fetches and merges product recommendations based on cart contents,
//             excludes products already in cart, and loads up to 10 unique recommendations
//             Optimizations: Caching, parallel fetch with timeout, debouncing, preloading
// Toggle: settings.show_cart_recommendations
// =========================================================================

// Cache for recommendations to prevent redundant API calls
/** @type {{ key: string | null, html: string | null, timestamp: number | null }} */
const recsCache = {
  key: null,       // Hash of current cart product IDs
  html: null,      // Cached recommendations HTML
  timestamp: null  // When cache was created
};

const CACHE_TTL = 30000; // 30 seconds cache lifetime
const FETCH_TIMEOUT = 2000; // 2 second timeout for slow responses

/**
 * Generate cache key from cart product IDs
 * @param {number[]} productIds - Array of product IDs
 * @returns {string} Cache key
 */
const getCacheKey = (productIds) => {
  return productIds.sort().join('-');
};

/**
 * Check if cache is valid for current cart state
 * @param {number[]} productIds - Array of product IDs
 * @returns {boolean} Whether cache is valid
 */
const isCacheValid = (productIds) => {
  if (!recsCache.key || !recsCache.html || !recsCache.timestamp) return false;
  
  const currentKey = getCacheKey(productIds);
  const age = Date.now() - recsCache.timestamp;
  
  return recsCache.key === currentKey && age < CACHE_TTL;
};

/**
 * Fetch recommendations with timeout protection
 * @param {string} url - URL to fetch
 * @param {number} timeout - Timeout in milliseconds
 * @returns {Promise<string>} Response text
 */
const fetchWithTimeout = (url, timeout = FETCH_TIMEOUT) => {
  return Promise.race([
    fetch(url).then(r => r.text()),
    new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Fetch timeout')), timeout)
    )
  ]);
};

// Track when loading state was shown
/** @type {number | null} */
let loadingStartTime = null;
const MIN_LOADING_DISPLAY_TIME = 300; // Minimum 300ms to show spinner

/**
 * Show loading state in recommendations container
 */
const showLoadingState = () => {
  const container = document.querySelector('[data-product-recommendations]');
  if (!container) return;
  
  loadingStartTime = Date.now();
  container.classList.add('cart-recommendations--loading');
  container.setAttribute('aria-busy', 'true');
};

/**
 * Hide loading state in recommendations container
 * Ensures minimum display time for better UX
 */
const hideLoadingState = () => {
  const container = document.querySelector('[data-product-recommendations]');
  if (!container) return;
  
  const hide = () => {
    container.classList.remove('cart-recommendations--loading');
    container.setAttribute('aria-busy', 'false');
  };
  
  // If loading state was shown, ensure minimum display time
  if (loadingStartTime) {
    const elapsed = Date.now() - loadingStartTime;
    const remaining = MIN_LOADING_DISPLAY_TIME - elapsed;
    
    if (remaining > 0) {
      setTimeout(hide, remaining);
    } else {
      hide();
    }
    
    loadingStartTime = null;
  } else {
    hide();
  }
};

/**
 * Loads and displays product recommendations in the cart drawer
 * Fetches recommendations for up to 3 products in cart and merges unique results
 * Optimized with caching, parallel fetching with timeout, and smart updates
 */
const loadCartRecommendations = async () => {
  const container = document.querySelector('[data-product-recommendations]');
  if (!container) return;

  const url = container.dataset.url;
  if (!url) return;

  try {
    // Get current cart state
    const cart = await fetch(window.Shopify.routes.root + 'cart.js').then(r => r.json());
    if (cart.item_count === 0) {
      hideLoadingState();
      container.innerHTML = '';
      recsCache.key = null;
      recsCache.html = null;
      recsCache.timestamp = null;
      return;
    }

    // Get up to 3 distinct product IDs from cart for recommendation queries
    const productIds = [...new Set(cart.items.map(item => item.product_id))].slice(0, 3);
    
    // Check cache first for instant display
    if (isCacheValid(productIds)) {
      // Use cached recommendations - no loading needed
      if (recsCache.html && container.innerHTML !== recsCache.html) {
        container.innerHTML = recsCache.html;
      }
      hideLoadingState();
      return;
    }
    
    // Show loading state when fetching fresh recommendations
    showLoadingState();
    
    // Fetch recommendations for each cart product in parallel with timeout protection
    const requests = productIds.map(id => {
      const fetchUrl = new URL(url, window.location.origin);
      fetchUrl.searchParams.set('product_id', id);
      return fetchWithTimeout(fetchUrl.toString()).catch(err => {
        return null; // Continue with other responses despite timeout/error
      });
    });

    const responses = await Promise.all(requests);
    
    // Filter out failed/timeout responses
    const validResponses = responses.filter(html => html !== null);
    
    if (validResponses.length === 0) {
      hideLoadingState();
      return; // Keep existing recommendations visible
    }
    
    // Parse and merge recommendations from all responses
    const parser = new DOMParser();
    const mergedItems = new Map();
    let containerHtml = '';

    validResponses.forEach(html => {
      const doc = parser.parseFromString(html, 'text/html');
      const recContainer = doc.querySelector('.cart-recommendations-container');
      
      if (recContainer) {
        // Keep the container structure from first response
        if (!containerHtml) containerHtml = recContainer.outerHTML;
        
        // Extract unique recommendation items
        const items = recContainer.querySelectorAll('.cart-recommendations__item');
        items.forEach(item => {
          const productCard = item.querySelector('product-card');
          const productId = productCard?.dataset.productId;
          if (productId && !mergedItems.has(productId)) {
            // Only include products not already in cart
            const inCart = cart.items.some(cartItem => cartItem.product_id == productId);
            if (!inCart) {
               mergedItems.set(productId, item.outerHTML);
            }
          }
        });
      }
    });

    // Check if we already have a recommendations container showing
    const existingContainer = container.querySelector('.cart-recommendations-container');
    const existingSlider = container.querySelector('.cart-recommendations__slider');
    
    if (mergedItems.size > 0) {
        // Build final recommendations HTML
        const productItemsHtml = Array.from(mergedItems.values()).slice(0, 10).join('');
        
        // If we already have a container, just update the slider content
        if (existingSlider) {
            existingSlider.innerHTML = productItemsHtml;
        } else {
            // First time loading - create full structure
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = containerHtml;
            const slider = tempDiv.querySelector('.cart-recommendations__slider');
            if (slider) {
                slider.innerHTML = productItemsHtml;
                container.innerHTML = tempDiv.innerHTML;
            }
        }
        
        // Cache the results
        recsCache.key = getCacheKey(productIds);
        recsCache.html = container.innerHTML;
        recsCache.timestamp = Date.now();
        
        // Hide loading state after successful load
        hideLoadingState();
    } else {
        // No new recommendations found
        // Hide loading state even if no recommendations
        hideLoadingState();
        
        // Only clear if cart is completely empty
        // Otherwise keep showing existing recommendations
        if (cart.item_count === 0 && existingContainer) {
          container.innerHTML = '';
        }
        // If cart has items but no new recommendations, keep existing ones visible
    }
  } catch (e) {
    console.error('Error loading recommendations:', e);
    hideLoadingState();
    // Don't clear recommendations on error - maintain user experience
  }
};

// Debouncing mechanism to prevent multiple simultaneous calls
/** @type {number | null} */
let loadTimeout = null;

const debouncedLoadRecommendations = () => {
  if (loadTimeout !== null) {
    clearTimeout(loadTimeout);
  }
  loadTimeout = setTimeout(loadCartRecommendations, 150);
};

// Consolidated event listeners using debounced handler
// This prevents redundant fetches when multiple events fire simultaneously

// Preload recommendations when items are added (background fetch)
document.addEventListener(CartAddEvent.eventName, () => {
  // Prefetch in background so recommendations are ready when drawer opens
  debouncedLoadRecommendations();
});

// Reload when cart is updated (quantity changes, removals)
document.addEventListener(ThemeEvents.cartUpdate, debouncedLoadRecommendations);

// Observe cart drawer open state to refresh on open
const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
             if (mutation.target.hasAttribute('open')) {
                 // Load immediately on open (will use cache if available)
                 loadCartRecommendations();
             }
        }
    });
});

// Attach observer to cart drawer dialog
const drawerDialog = document.querySelector('.cart-drawer__dialog');
if (drawerDialog) {
    observer.observe(drawerDialog, { attributes: true });
} else {
    // Fallback: wait for dialog to appear in DOM
    setTimeout(() => {
        const drawerDialogDeferred = document.querySelector('.cart-drawer__dialog');
        if (drawerDialogDeferred) observer.observe(drawerDialogDeferred, { attributes: true });
    }, 2000);
}

// END – Dynamic Cart Recommendations Loading
// =========================================================================
